<template>
  <Dialog
    is-persistent
    :title="dialogTitle"
    :dialog="isOpened"
    :confirmation-button-text="confirmationButtonText"
    @confirmationAction="update"
  >
    <template #content>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-row class="pb-4">
          <v-col>
            <v-tooltip
              bottom
              :disabled="dailyManager !== null"
            >
              <template v-slot:activator="{ on, attrs }">
                <span
                  style="display: inline-flex;"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-checkbox
                    class="select-item-checkbox mr-2"
                    color="primary"
                    hide-details
                    label="Daily overtime allowed"
                    :input-value="dailyOvertimeAllowed"
                    :disabled="dailyManager === null"
                    @change="onChangeDailyAllow"
                  />
                </span>
              </template>
              <span v-if="dailyManager === null">
                Please select manager before change
              </span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row class="pb-4">
          <v-col>
            <v-autocomplete
              v-model="dailyManager"
              class="secondary-background"
              label="Daily admit manager"
              item-text="name"
              item-value="id"
              hide-details
              outlined
              dense
              clearable
              :filter="filterManagerByName"
              :menu-props="{ offsetY: true }"
              :items="managers"
              :value="dailyManager"
              @change="onDailyManagerChange"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-divider />
        <v-row class="pb-4 pt-2">
          <v-col>
            <v-tooltip
              bottom
              :disabled="monthlyManager !== null"
            >
              <template v-slot:activator="{ on, attrs }">
                <span
                  style="display: inline-flex;"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-checkbox
                    class="select-item-checkbox mr-2"
                    color="primary"
                    hide-details
                    label="Monthly overtime allowed"
                    :disabled="monthlyManager === null"
                    :input-value="monthlyOvertimeAllowed"
                    @change="onChangeMonthlyAllow"
                  />
                </span>
              </template>
              <span v-if="monthlyManager === null">
                Please select manager before change
              </span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row class="pb-4">
          <v-col>
            <v-autocomplete
              v-model="monthlyManager"
              class="secondary-background"
              label="Monthly admit manager"
              item-text="name"
              item-value="id"
              hide-details
              outlined
              dense
              clearable
              :filter="filterManagerByName"
              :menu-props="{ offsetY: true }"
              :items="managers"
              :value="monthlyManager"
              @change="onMonthlyManagerChange"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </Dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex';

import { Dialog } from '@/components/shared';
import { NotificationHelper } from '@/helpers/notification.helper';

export default {
  components: {
    Dialog,
  },
  props: {
    isOpened: {
      type: Boolean,
      required: true,
    },
    managers: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      dialogTitle: 'Employee overtime settings',
      confirmationButtonText: 'Update',
      valid: true,
      dailyOvertimeAllowed: false,
      monthlyOvertimeAllowed: false,
      monthlyManager: null,
      dailyManager: null,
    };
  },
  computed: {
    ...mapState('employees/single', ['employee', 'employeeOvertimeFlags']),
  },
  watch: {
    employee() {
      this.dailyOvertimeAllowed = this.employee?.is_daily_overtime_allowed;
      this.monthlyOvertimeAllowed = this.employee?.is_monthly_overtime_allowed;
    },
    employeeOvertimeFlags() {
      if (this.employeeOvertimeFlags.monthly_overtime_decider_id) {
        this.monthlyManager = this.employeeOvertimeFlags.monthly_overtime_decider_id;
      }

      if (this.employeeOvertimeFlags.daily_overtime_decider_id) {
        this.dailyManager = this.employeeOvertimeFlags.daily_overtime_decider_id;
      }
    },
  },
  async created() {
    await this.getEmployee({ employeeId: this.$route.params.id });
  },
  methods: {
    ...mapActions('employees/single', [
      'updateEmployee',
      'getEmployeeOvertimeFlags',
      'getEmployee',
    ]),
    filterManagerByName(item, queryText) {
      if (!queryText) return true;

      const normalizedText = queryText.toLowerCase().trim();
      const normalizedName = item.name.toLowerCase();

      return normalizedName.indexOf(normalizedText) > -1;
    },
    async onMonthlyManagerChange(monthlyManagerId) {
      this.monthlyManager = monthlyManagerId;
    },
    async onDailyManagerChange(dailyManagerId) {
      this.dailyManager = dailyManagerId;
    },
    async onChangeDailyAllow(value) {
      if (this.dailyManager) {
        this.dailyOvertimeAllowed = value;
      } else {
        NotificationHelper.showError('Please choose manager before change');
      }
    },
    async onChangeMonthlyAllow(value) {
      if (this.monthlyManager) {
        this.monthlyOvertimeAllowed = value;
      } else {
        NotificationHelper.showError('Please choose manager before change');
      }
    },
    async update(confirmed) {
      if (!confirmed) {
        return this.closeDialog();
      }

      await this.updateEmployee({
        employeeId: this.employee.id,
        patch: {
          is_daily_overtime_allowed: this.dailyOvertimeAllowed,
          daily_overtime_manager_id: this.dailyManager,
          is_monthly_overtime_allowed: this.monthlyOvertimeAllowed,
          monthly_overtime_manager_id: this.monthlyManager,
        },
      });
      await this.closeDialog();
    },
    async closeDialog() {
      this.$emit('close');
      await this.getEmployee({ employeeId: this.$route.params.id });
      await this.getEmployeeOvertimeFlags({ employeeId: this.$route.params.id });
    },
  },
};
</script>
